import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getAdminCredential, getFileUrl } from '../lib/api';
import { Loader2, ArrowLeft, Download, Printer, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { QRCodeCanvas } from 'qrcode.react';

const LOGO = '/logo.png';

// Calculate 1-year expiration from an ISO date string
const getExpirationDate = (issuedAt) => {
  try {
    const d = issuedAt ? new Date(issuedAt) : new Date();
    d.setFullYear(d.getFullYear() + 1);
    return d;
  } catch {
    const d = new Date();
    d.setFullYear(d.getFullYear() + 1);
    return d;
  }
};

const formatDate = (d) => d.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' });

export default function CredentialPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const cardRef = useRef(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch_ = useCallback(async () => {
    try { const { data: d } = await getAdminCredential(id); setData(d); }
    catch { toast.error('Error al cargar credencial'); nav('/admin'); }
    finally { setLoading(false); }
  }, [id, nav]);

  useEffect(() => { if (!user || user.role !== 'admin') { nav('/'); return; } fetch_(); }, [user, nav, fetch_]);

  const handlePrint = () => { window.print(); };

  const handleDownload = async () => {
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(cardRef.current, { scale: 3, useCORS: true, backgroundColor: '#FFFFFF', logging: false });
      const link = document.createElement('a');
      link.download = `credencial-rp-${data.user.user_id_code || data.user.name}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('Credencial descargada');
    } catch (e) {
      console.error(e);
      toast.error('Error al descargar. Usa la opción de imprimir.');
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const [html2canvas, { jsPDF }] = await Promise.all([
        import('html2canvas').then(m => m.default),
        import('jspdf'),
      ]);
      const canvas = await html2canvas(cardRef.current, { scale: 3, useCORS: true, backgroundColor: '#FFFFFF', logging: false });
      const img = canvas.toDataURL('image/png');
      // Credential aspect ~ vertical card. Use A6 (105×148mm) portrait.
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a6' });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 6;
      const w = pageW - margin * 2;
      const h = (canvas.height / canvas.width) * w;
      const finalH = Math.min(h, pageH - margin * 2);
      const finalW = (canvas.width / canvas.height) * finalH;
      pdf.addImage(img, 'PNG', (pageW - finalW) / 2, (pageH - finalH) / 2, finalW, finalH);
      pdf.save(`credencial-rp-${data.user.user_id_code || data.user.name}.pdf`);
      toast.success('PDF descargado');
    } catch (e) {
      console.error(e);
      toast.error('Error al generar PDF');
    }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="h-7 w-7 animate-spin text-[#E50B15]" /></div>;
  if (!data) return null;

  const u = data.user;
  const photoSrc = u.photo_url ? (u.photo_url.startsWith('http') ? u.photo_url : getFileUrl(u.photo_url)) : null;
  const issuedAt = u.created_at || data.generated_at;
  const expDate = getExpirationDate(issuedAt);
  const issuedDate = issuedAt ? new Date(issuedAt) : new Date();
  const roleLabel = u.role === 'admin' ? 'ADMINISTRADOR' : 'PERIODISTA';
  const verifyCode = u.user_id_code || '';
  const verifyUrl = verifyCode ? `${window.location.origin}/verificar/${verifyCode}` : '';

  return (
    <div data-testid="credential-page" className="max-w-3xl mx-auto px-4 py-8">
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-1 text-[10px] font-black text-[#0B1F40]/30 hover:text-[#E50B15] tracking-[0.15em] uppercase mb-6 print:hidden">
        <ArrowLeft className="h-3 w-3" /> VOLVER
      </button>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2 mb-6 print:hidden">
        <button data-testid="download-credential" onClick={handleDownload}
          className="bg-[#E50B15] text-white text-[10px] font-black h-9 px-5 flex items-center gap-1.5 brutal-btn-press" style={{ boxShadow: '3px 3px 0 #0B1F40' }}>
          <Download className="h-3.5 w-3.5" /> DESCARGAR PNG
        </button>
        <button data-testid="download-credential-pdf" onClick={handleDownloadPDF}
          className="bg-white border-2 border-[#0B1F40] text-[#0B1F40] text-[10px] font-black h-9 px-5 flex items-center gap-1.5 brutal-btn-press hover:bg-[#0B1F40] hover:text-white transition-colors" style={{ boxShadow: '3px 3px 0 #E50B15' }}>
          <FileText className="h-3.5 w-3.5" /> DESCARGAR PDF
        </button>
        <button data-testid="print-credential" onClick={handlePrint}
          className="bg-[#0B1F40] text-white text-[10px] font-black h-9 px-5 flex items-center gap-1.5 brutal-btn-press" style={{ boxShadow: '3px 3px 0 #E50B15' }}>
          <Printer className="h-3.5 w-3.5" /> IMPRIMIR
        </button>
      </div>

      {/* Credential Card */}
      <div ref={cardRef} data-testid="credential-card" className="mx-auto" style={{ maxWidth: '420px' }}>
        <div className="border-2 border-[#0B1F40] overflow-hidden bg-white relative" style={{ boxShadow: '6px 6px 0 #0B1F40' }}>
          {/* Header */}
          <div className="bg-[#0B1F40] px-4 py-3 flex items-center gap-3">
            <img src={LOGO} alt="RP" className="h-9 w-9" crossOrigin="anonymous" />
            <div className="flex-1">
              <p className="text-white font-black text-sm leading-none tracking-tight" style={{ fontFamily: 'Archivo Black' }}>
                <span className="text-[#E50B15]">RP</span> NOTICIAS
              </p>
              <p className="text-white/40 text-[7px] tracking-[0.25em] uppercase mt-1">La crítica de los hechos</p>
            </div>
            <div className="text-right">
              <p className="text-[7px] text-white/40 tracking-[0.2em] uppercase">CREDENCIAL</p>
              <p className="text-[#E50B15] font-black text-[10px] tracking-tight">OFICIAL</p>
            </div>
          </div>

          {/* Body with Watermark */}
          <div className="relative">
            {/* Watermark — diagonal logo text, subtle */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
              <div className="flex flex-col gap-1 opacity-[0.05] rotate-[-28deg] whitespace-nowrap">
                <p className="text-[#0B1F40] font-black text-5xl tracking-tighter leading-none" style={{ fontFamily: 'Archivo Black' }}>RP NOTICIAS</p>
                <p className="text-[#0B1F40] font-black text-5xl tracking-tighter leading-none" style={{ fontFamily: 'Archivo Black' }}>RP NOTICIAS</p>
                <p className="text-[#0B1F40] font-black text-5xl tracking-tighter leading-none" style={{ fontFamily: 'Archivo Black' }}>RP NOTICIAS</p>
              </div>
            </div>

            {/* Content */}
            <div className="relative z-10 p-5">
              {/* Photo */}
              <div className="flex justify-center mb-4">
                <div className="w-32 h-36 border-2 border-[#0B1F40] bg-[#F4F5F7] overflow-hidden flex items-center justify-center" style={{ boxShadow: '3px 3px 0 #E50B15' }}>
                  {photoSrc ? (
                    <img src={photoSrc} alt={u.name} className="w-full h-full object-cover" crossOrigin="anonymous" />
                  ) : (
                    <div className="text-center">
                      <p className="text-[#0B1F40]/20 text-4xl font-black" style={{ fontFamily: 'Archivo Black' }}>{u.name?.charAt(0) || '?'}</p>
                      <p className="text-[8px] text-[#0B1F40]/20 font-bold mt-1">SIN FOTO</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Name */}
              <div className="text-center mb-3">
                <p className="text-[8px] text-[#0B1F40]/40 font-black tracking-[0.25em] uppercase mb-0.5">NOMBRE</p>
                <p data-testid="credential-name" className="text-lg font-black text-[#0B1F40] leading-tight uppercase" style={{ fontFamily: 'Archivo Black' }}>
                  {u.name}
                </p>
              </div>

              {/* Role */}
              <div className="text-center mb-3">
                <p className="text-[8px] text-[#0B1F40]/40 font-black tracking-[0.25em] uppercase mb-0.5">CARGO</p>
                <p data-testid="credential-role" className="inline-block bg-[#E50B15] text-white text-xs font-black tracking-[0.15em] px-3 py-1" style={{ fontFamily: 'Archivo Black' }}>
                  {roleLabel}
                </p>
              </div>

              {/* ID */}
              <div className="text-center mb-4">
                <p className="text-[8px] text-[#0B1F40]/40 font-black tracking-[0.25em] uppercase mb-0.5">IDENTIFICACIÓN</p>
                <p data-testid="credential-id" className="text-xl font-black text-[#0B1F40] tracking-tight" style={{ fontFamily: 'Archivo Black' }}>
                  {u.user_id_code || 'RP-——'}
                </p>
              </div>

              {/* Validity + QR */}
              <div className="border-t-2 border-[#0B1F40]/10 pt-3 grid grid-cols-3 gap-2 items-center">
                <div className="text-center">
                  <p className="text-[7px] text-[#0B1F40]/40 font-black tracking-[0.2em] uppercase mb-0.5">EMISIÓN</p>
                  <p data-testid="credential-issued" className="text-[10px] font-black text-[#0B1F40]">{formatDate(issuedDate)}</p>
                </div>
                <div className="flex flex-col items-center" data-testid="credential-qr">
                  {verifyUrl ? (
                    <div className="bg-white p-1 border border-[#0B1F40]/20">
                      <QRCodeCanvas value={verifyUrl} size={60} level="M" bgColor="#FFFFFF" fgColor="#0B1F40" includeMargin={false} />
                    </div>
                  ) : (
                    <div className="w-[60px] h-[60px] bg-[#F4F5F7] border border-[#0B1F40]/20" />
                  )}
                  <p className="text-[6px] text-[#0B1F40]/50 font-black tracking-[0.2em] uppercase mt-1">VERIFICAR</p>
                </div>
                <div className="text-center">
                  <p className="text-[7px] text-[#0B1F40]/40 font-black tracking-[0.2em] uppercase mb-0.5">VIGENCIA</p>
                  <p data-testid="credential-expires" className="text-[10px] font-black text-[#E50B15]">{formatDate(expDate)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-[#E50B15] px-4 py-2 flex items-center justify-between relative z-10">
            <p className="text-[8px] text-white font-black tracking-[0.2em]">RPNOTICIA.COM/VERIFICAR</p>
            <p className="text-[8px] text-white/70 font-bold tracking-wider">VÁLIDA 1 AÑO</p>
          </div>
        </div>

        {/* Helper text */}
        <p className="text-center text-[9px] text-[#0B1F40]/30 font-bold tracking-wider mt-4 print:hidden">
          ESTA CREDENCIAL ES PROPIEDAD DE RP NOTICIAS — USO EXCLUSIVO DEL PORTADOR
        </p>
      </div>
    </div>
  );
}
